import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getEventBySlug, getChildEvents, getEventById } from '../lib/api/events';
import type { EventWithDetails, Event } from '../types/database';
import { captureError } from '../lib/capture-error';
import EventDescription from '../components/EventDescription';
import { getEventPhase, hasEventEnded, isRegistrationOpen } from '../lib/event-status';
import EventPhaseBadge from '../components/EventPhaseBadge';
import { formatLocalDate, formatLocalDateRange, formatSignupOptionSchedule } from '../lib/dates';
import { buildEventApplyPath, formatSignupPrice, hasSignupPrice } from '../lib/signup-options';
import './EventDetailPage.css';

/** Build display event for a child: apply inherit_from_parent so empty child fields use parent's. */
function mergeChildWithParent(
  child: EventWithDetails,
  parent: EventWithDetails
): EventWithDetails {
  const inherit = child.inherit_from_parent ?? {};
  const shouldUseParent = (key: string) => inherit[key] === true;

  const hasValue = (v: unknown): boolean => {
    if (v == null) return false;
    if (typeof v === 'string') return v.trim().length > 0;
    if (Array.isArray(v)) return v.length > 0;
    return true;
  };

  const display: EventWithDetails = { ...child };

  if (shouldUseParent('description') && !hasValue(child.description))
    display.description = parent.description;
  if (shouldUseParent('banner_image_url') && !hasValue(child.banner_image_url))
    display.banner_image_url = parent.banner_image_url;
  if (shouldUseParent('featured_image_url') && !hasValue(child.featured_image_url))
    display.featured_image_url = parent.featured_image_url;
  if (shouldUseParent('flyer_url') && !hasValue(child.flyer_url))
    display.flyer_url = parent.flyer_url;

  if (shouldUseParent('schedule') && !hasValue(child.schedule_items))
    display.schedule_items = parent.schedule_items ?? [];
  if (shouldUseParent('contacts') && !hasValue(child.contacts))
    display.contacts = parent.contacts ?? [];

  return display;
}

const EventDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [event, setEvent] = useState<EventWithDetails | null>(null);
  const [childEvents, setChildEvents] = useState<Event[]>([]);
  const [parentEventWithDetails, setParentEventWithDetails] = useState<EventWithDetails | null>(null);
  /** When viewing a child event: all locations (siblings + self) for "Change your location?" */
  const [locationOptions, setLocationOptions] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEvent = useCallback(async () => {
    if (!slug) return;

    try {
      setLoading(true);
      setError(null);

      const eventData = await getEventBySlug(slug);
      if (!eventData) {
        setError('Event not found');
        return;
      }

      setEvent(eventData);

      // Load child events if this is a parent
      if (eventData.is_parent_event) {
        const children = await getChildEvents(eventData.id);
        setChildEvents(children);
        setLocationOptions([]);
      } else {
        setChildEvents([]);
      }

      // Load parent and sibling locations if this is a child (for inheritance + "Change your location?")
      if (eventData.parent_event_id) {
        const [parent, siblings] = await Promise.all([
          getEventById(eventData.parent_event_id),
          getChildEvents(eventData.parent_event_id),
        ]);
        setParentEventWithDetails(parent ?? null);
        setLocationOptions(siblings);
      } else {
        setParentEventWithDetails(null);
        setLocationOptions([]);
      }
    } catch (err) {
      captureError(err, 'Failed to load event');
      setError('Failed to load event details');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const displayEvent = useMemo((): EventWithDetails | null => {
    if (!event) return null;
    if (event.parent_event_id && parentEventWithDetails) {
      return mergeChildWithParent(event, parentEventWithDetails);
    }
    return event;
  }, [event, parentEventWithDetails]);

  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  if (loading) {
    return (
      <div className="container my-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container my-5">
        <div className="alert alert-danger text-center">
          {error || 'Event not found'}
        </div>
        <div className="text-center">
          <Link to="/events" className="btn btn-primary">
            <i className="fas fa-calendar-alt me-2" aria-hidden></i>
            View All Events
          </Link>
        </div>
      </div>
    );
  }

  const isParentEvent = event.is_parent_event;
  const hasChildEvents = childEvents.length > 0;
  const isChildEvent = !!parentEventWithDetails;
  const display = displayEvent ?? event;
  const locationDisplay = [display.location_city, display.location_state, display.location_country]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
  const dateRange =
    formatLocalDateRange(display.start_date, display.end_date) ?? '';
  const deadlineMs = new Date(display.registration_deadline).getTime();
  const twoWeeksFromNow = Date.now() + 14 * 24 * 60 * 60 * 1000;
  const deadlineClass = deadlineMs > twoWeeksFromNow ? 'text-success' : 'text-danger';

  const ended = hasEventEnded(display);
  const isDraftPreview = display.status === 'draft';
  const registrationOpen = isDraftPreview || isRegistrationOpen(display);
  // For parent events, don't show the apply button
  const canApply = !isParentEvent && !event.is_legacy_event && registrationOpen;

  return (
    <div className="container my-5">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          {/* Banner Image - at top */}
          {display.banner_image_url && (
            <div className="mb-4">
              <img
                src={display.banner_image_url}
                alt={display.title}
                className="img-fluid rounded d-block mx-auto"
                style={{ maxHeight: '400px' }}
              />
            </div>
          )}

          {/* Breadcrumb for child events only - keeps rest of page identical to standalone */}
          {isChildEvent && parentEventWithDetails && (
            <p className="text-center text-muted small mb-2">
              Part of{' '}
              <Link to={`/events/${parentEventWithDetails.slug}`} className="text-decoration-none event-detail-parent-link">
                {parentEventWithDetails.title}
              </Link>
            </p>
          )}

          {/* Event name */}
          <div className="text-center mb-2">
            <EventPhaseBadge phase={getEventPhase(display)} />
          </div>
          <h1 className="text-center mb-3">{display.title}</h1>

          {!registrationOpen && (
            <div className="alert alert-secondary text-center" role="status">
              {ended
                ? 'This outreach has ended. Thank you to everyone who served!'
                : 'Registration for this outreach is closed.'}{' '}
              <Link to="/events">See upcoming outreaches</Link>
            </div>
          )}

          {/* For parent events: show child event locations */}
          {isParentEvent && hasChildEvents && (
            <div className="card mb-4">
              <div className="card-body">
                <h4>{ended ? 'Locations' : 'Choose your location'}</h4>
                <p className="text-muted mb-3">
                  {ended
                    ? 'This outreach took place in multiple cities.'
                    : 'This event takes place in multiple cities. Select a location to see details and register for that site.'}
                </p>
                <div className="d-flex flex-wrap gap-2">
                  {childEvents
                    .sort((a, b) => a.display_order - b.display_order)
                    .map((child) => {
                      const locationLabel = [child.location_city, child.location_state]
                        .filter(Boolean)
                        .join(', ') || child.title;
                      return (
                        <Link
                          key={child.id}
                          to={`/events/${child.slug}`}
                          className={`btn ${ended ? 'btn-outline-secondary' : 'btn-success'}`}
                        >
                          <i className="fas fa-map-marker-alt me-2"></i>
                          {locationLabel}
                        </Link>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {/* Event Details - location, date, deadline, description */}
          <div className="card mb-4">
            <div className="card-body">
              <h4>Event Details</h4>
              {(locationDisplay || dateRange) && !isParentEvent && (
                <p className="text-muted mb-3">
                  {locationDisplay && <span>{locationDisplay}</span>}
                  {locationDisplay && dateRange && ' • '}
                  {dateRange && <span>{dateRange}</span>}
                </p>
              )}
              {isParentEvent && (
                <p className="text-muted mb-3">
                  <span>{ended ? 'Multiple locations' : 'Multiple locations available'}</span>
                  {dateRange && ' • '}
                  {dateRange && <span>{dateRange}</span>}
                </p>
              )}
              {!isParentEvent && !ended && (
                <p className="mb-3">
                  <strong>Registration deadline:</strong>{' '}
                  <span className={deadlineClass}>
                    <i className="fas fa-clock me-1" aria-hidden></i>
                    {formatLocalDate(display.registration_deadline)}
                  </span>
                </p>
              )}
              <EventDescription text={display.description} />
            </div>
          </div>

          {/* Registration Options - display only */}
          {canApply && display.signup_options && display.signup_options.length > 0 && (
            <div className="card mb-4">
              <div className="card-body">
                <h4>Registration Options</h4>
                <div className="row">
                  {display.signup_options
                    .sort((a, b) => a.display_order - b.display_order)
                    .map((option) => {
                      const optionDates = formatSignupOptionSchedule(option);

                      return (
                        <div key={option.id} className="col-md-4 mb-3">
                          <Link
                            to={buildEventApplyPath(event.slug, option.id)}
                            className="registration-option-card border rounded p-3 h-100 d-block text-decoration-none text-body"
                          >
                            <h5 className="mb-1">{option.label}</h5>
                            {optionDates && (
                              <p className="mb-1 small text-muted">{optionDates}</p>
                            )}
                            {hasSignupPrice(option.price) && (
                              <p className="fs-4 fw-bold text-primary mb-0">
                                {formatSignupPrice(option.price)}
                              </p>
                            )}
                            {option.description && (
                              <p className="mb-0 small text-muted mt-2">{option.description}</p>
                            )}
                          </Link>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {/* CTA - Apply Button */}
          {canApply && (
            <div className="card mb-4">
              <div className="card-body text-center">
                <Link
                  to={`/events/${event.slug}/apply`}
                  className="btn btn-success btn-lg"
                >
                  <i className="fas fa-clipboard-check me-2" aria-hidden></i>
                  Register / Apply
                </Link>
                {display.flyer_url && (
                  <a
                    href={display.flyer_url}
                    className="btn btn-outline-secondary btn-lg ms-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fas fa-download me-2" aria-hidden></i>
                    Download Flyer
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Legacy event CTA */}
          {event.is_legacy_event && event.legacy_application_url && registrationOpen && (
            <div className="card mb-4">
              <div className="card-body text-center">
                <a
                  href={event.legacy_application_url}
                  className="btn btn-success btn-lg"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-clipboard-check me-2" aria-hidden></i>
                  Register / Apply
                </a>
                {display.flyer_url && (
                  <a
                    href={display.flyer_url}
                    className="btn btn-outline-secondary btn-lg ms-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fas fa-download me-2" aria-hidden></i>
                    Download Flyer
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Schedule */}
          {display.schedule_items && display.schedule_items.length > 0 && (
            <div className="card mb-4">
              <div className="card-body">
                <h4>Event Schedule</h4>
                <ul className="list-unstyled">
                  {display.schedule_items
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((item) => (
                      <li key={item.id} className="mb-3">
                        <strong>
                          {item.title}
                          {item.time_start && ` - ${item.time_start}`}
                        </strong>
                        {item.description && <p className="mb-0">{item.description}</p>}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          )}

          {/* Contacts */}
          {display.contacts && display.contacts.length > 0 && (
            <div className="card mb-4">
              <div className="card-body">
                <h4>Contact Information</h4>
                {display.contacts
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((contact) => (
                    <div key={contact.id} className="mb-3">
                      <p className="mb-1">
                        <strong>{contact.name}</strong>
                        {contact.description && ` - ${contact.description}`}
                      </p>
                      {contact.phone && (
                        <p className="mb-1">
                          Phone: <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                        </p>
                      )}
                      {contact.email && (
                        <p className="mb-1">
                          Email: <a href={`mailto:${contact.email}`}>{contact.email}</a>
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Gallery Images */}
          {display.gallery_images && display.gallery_images.length > 0 && (
            <div className="card mb-4">
              <div className="card-body">
                <h4>Event Gallery</h4>
                <div className="row">
                  {display.gallery_images
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((image) => (
                      <div key={image.id} className="col-md-4 mb-3">
                        <img
                          src={image.image_url}
                          alt={image.caption || display.title}
                          className="img-fluid rounded"
                        />
                        {image.caption && (
                          <p className="text-center mt-2 small text-muted">{image.caption}</p>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* Child pages: change location at bottom */}
          {isChildEvent && locationOptions.length > 1 && (
            <div className="card mb-4">
              <div className="card-body">
                <h4>Change your location?</h4>
                <p className="text-muted mb-3">
                  Select a different location to see details and register there.
                </p>
                <div className="d-flex flex-wrap gap-2">
                  {locationOptions
                    .sort((a, b) => a.display_order - b.display_order)
                    .map((loc) => {
                      const locationLabel = [loc.location_city, loc.location_state]
                        .filter(Boolean)
                        .join(', ') || loc.title;
                      const isCurrent = event && loc.id === event.id;
                      return isCurrent ? (
                        <span
                          key={loc.id}
                          className="btn btn-outline-secondary disabled"
                          aria-current="location"
                        >
                          <i className="fas fa-map-marker-alt me-2"></i>
                          {locationLabel} (current)
                        </span>
                      ) : (
                        <Link
                          key={loc.id}
                          to={`/events/${loc.slug}`}
                          className="btn btn-outline-primary"
                        >
                          <i className="fas fa-map-marker-alt me-2"></i>
                          {locationLabel}
                        </Link>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {/* Back to parent link for child events */}
          {isChildEvent && parentEventWithDetails && (
            <div className="text-center mb-4">
              <Link
                to={`/events/${parentEventWithDetails.slug}`}
                className="btn btn-outline-secondary"
              >
                <i className="fas fa-arrow-left me-2"></i>
                Return to {parentEventWithDetails.title}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetailPage;
